import { formatujCzasTrwania } from "./formatNagrania.js";

/**
 * Ostrzezenie o koszcie dlugiego nagrania. Ocena brzmienia (model audio) liczy sie OSOBNO dla kazdej wypowiedzi, wiec
 * liczba platnych wywolan rosnie z dlugoscia rozmowy, a przy wielogodzinnym nagraniu sa to tysiace - zwykle znacznie
 * wiecej niz kosztuje sama transkrypcja. Uzytkownik ma to zobaczyc PRZED kliknieciem "Analizuj", a nie na fakturze.
 *
 * Wszystko tu jest czyste: prog, tekst i decyzja o domyslnym wyborze (tests/dom/ostrzezenieKosztu.test.ts).
 */

/** Powyzej tylu sekund (2 godzin) nagranie dostaje ostrzezenie i domyslnie "bez oceny brzmienia". */
export const PROG_OSTRZEZENIA_KOSZTU_SEK = 2 * 3600;

/** Wartosc w wyborze dostawcy ekspresji oznaczajaca "nie oceniaj brzmienia" (ProviderPicker, rejestr dostawcow). */
export const BRAK_OCENY_BRZMIENIA = "brak";

/** Czy znamy czas i jest dluzszy niz prog. Nieznany czas (null) to NIE "dlugie": nie ostrzegamy na slepo. */
export function czyDlugieNagranie(czasSek: number | null): czasSek is number {
  return czasSek !== null && czasSek > PROG_OSTRZEZENIA_KOSZTU_SEK;
}

export interface WyborEkspresji {
  dostawca: string;
  model: string;
}

/**
 * Jaki dostawca oceny brzmienia obowiazuje dla tego nagrania.
 *
 * - Uzytkownik zmienil wybor sam (`recznie`): jego decyzja wygrywa ZAWSZE, takze przy dlugim nagraniu.
 * - Nagranie dluzsze niz prog, wybor nietkniety: "bez oceny brzmienia" (model sie zeruje razem z dostawca).
 * - Reszta (krotkie albo nieznany czas): wybor z ustawien, bez zmian.
 *
 * Wynik jest WYLICZANY, a nie zapisywany do stanu: po usunieciu dlugiego pliku albo wyborze krotkiego formularz
 * sam wraca do ustawien zapisanych, bez osobnej logiki przywracania.
 */
export function wyborEkspresjiDlaNagrania(wejscie: {
  czasSek: number | null;
  recznie: boolean;
  aktualny: WyborEkspresji;
}): WyborEkspresji {
  if (wejscie.recznie) return wejscie.aktualny;
  if (czyDlugieNagranie(wejscie.czasSek)) return { dostawca: BRAK_OCENY_BRZMIENIA, model: "" };
  return wejscie.aktualny;
}

/**
 * Tekst banera. Dwa warianty, bo "wylaczylismy ocene brzmienia" byloby nieprawda, gdy uzytkownik wlaczyl ja z powrotem.
 */
export function tekstOstrzezeniaKosztu(czasSek: number, ocenaBrzmieniaWlaczona: boolean): string {
  const czas = formatujCzasTrwania(czasSek);
  const wyjasnienie =
    "Ocena brzmienia woła model audio osobno dla każdej wypowiedzi, więc przy takim nagraniu to tysiące płatnych wywołań i zwykle znacznie wyższy koszt niż sama transkrypcja.";
  // Skrot "godz." ma wlasna kropke: bez tego wychodzi "3 godz.." na koncu zdania.
  const kropka = czas.endsWith(".") ? "" : ".";
  if (ocenaBrzmieniaWlaczona) {
    return `To nagranie trwa ${czas}${kropka} ${wyjasnienie} Masz włączoną ocenę brzmienia - jeśli nie jest ci potrzebna, wyłącz ją w oknie Zmień modele.`;
  }
  return `To nagranie trwa ${czas}${kropka} ${wyjasnienie} Dlatego ustawiliśmy dla niego "bez oceny brzmienia". Możesz ją włączyć w oknie Zmień modele.`;
}
